import React, { useEffect, useContext, useState, useCallback }  from 'react'
import { View, StyleSheet, ScrollView, Platform, TouchableOpacity, Alert } from 'react-native'
import { Select, Text, Button, Spinner, SelectItem, Icon, Card, Layout, RadioGroup, Radio, useTheme } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import HeaderIconButton from '../../components/HeaderIconButton'
import { useAtomValue } from 'jotai'
import * as DocumentPicker from 'expo-document-picker'
// Conditional import for web only
const ReactDropzone = Platform.select({
  web: () => require('react-dropzone'),
  default: () => null,
})?.()
import { localeDateServiceAtom, productCategoriesDictAtom, productUnitsDictAtom, productsByBarcodeDictAtom, productsDictAtom, storesDictAtom, suppliersDictAtom, userRoleAtom } from '../../global/AtomsProvider'
import { saveNewProduct, saveNewProductCategory, saveNewSupplier, updateProduct } from '../../global/Firebase/firebase'
import { getDailyStatID } from '../../utils/timezoneFunctions'
import { useTranslation } from 'react-i18next'
import StoreChooserHorizontal from '../../components/StoreChooserHorizontal'
import { Table, Row } from 'react-native-table-component'
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { HintView } from '../../components/HintView'
import { exportToCSV, exportToExcel } from '../../utils/exportUtils'
import {
  IMPORT_DOCUMENT_PICKER_TYPES,
  IMPORT_DROPZONE_ACCEPT,
  MalformedImportFileError,
  readImportFileFromUri,
  readImportFileFromWeb,
} from '../../utils/importFileReader'
// ATL-968: export in catalog order, not dict-insertion order.
import { getProductsList } from '../../utils/productListHelpers'
import { format } from 'date-fns'

export default function ExportImportScreen({ navigation }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [context, setContext] = useContext(UserContext)
  const db = context.db
  const productCategoriesDict = useAtomValue(productCategoriesDictAtom)
  const productsDict = useAtomValue(productsDictAtom)
  const productsByBarcodeDict = useAtomValue(productsByBarcodeDictAtom)
  const suppliersDict = useAtomValue(suppliersDictAtom)
  const storesDict = useAtomValue(storesDictAtom)
  const userRole = useAtomValue(userRoleAtom)
  const productUnitsDict = useAtomValue(productUnitsDictAtom)
  const [productsExport, setProductsExport] = useState()
  const [productsExportHeaders, setProductsExportHeaders] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const [store, setStore] = useState(context.user.store?.id)
  const [importedData, setImportedData] = useState(null)
  const [importedColumns, setImportedColumns] = useState([])
  const [columnMappingByName, setColumnMappingByName] = useState({})
  const [showColumnMappingUI, setShowColumnMappingUI] = useState(false)
  const [showCategoriesAndSuppliersUI, setShowCategoriesAndSuppliersUI] = useState(false)
  const [productCategoryMapping, setProductCategoryMapping] = useState({})
  const productCategoriesWithAdditionalOptions = [...Object.values(productCategoriesDict), {text: t('exportImport.createNewCategory'), id: 'new'}]
  const [supplierMapping, setSupplierMapping] = useState({})
  const suppliersWithAdditionalOptions = [...Object.values(suppliersDict), {text: t('exportImport.createNewSupplier'), id: 'new'}]
  const [existingProductsUpdateSettings, setExistingProductsUpdateSettings] = useState(true)
  const [importProgress, setImportProgress] = useState(0)

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
    if (!userRole?.permissions?.['exportImport'] && context.user?.role !== 'admin') {
      navigation.navigate('Home')
    }
  }, [])

  const generateProductExport = async (type = 'csv') => {
    // First create the data with translated headers
    let exportData = getProductsList(productsDict).map(product => ({
      [t('exportImport.name')]: product.name,
      [t('exportImport.price')]: product.salePrices[store],
      [t('exportImport.purchasePrice')]: product.purchasePrice,
      [t('exportImport.barcode')]: product.barcode,
      [t('exportImport.category')]: productCategoriesDict[product.categoryID]?.name,
      [t('exportImport.supplier')]: suppliersDict[product.supplierID]?.name,
      [t('exportImport.inStock')]: product.storeStock[store],
      [t('exportImport.externalId')]: product.id,
    }))

    try {
      const filename = 'products-export-' + format(new Date(), 'd-M-y-HH-mm')
      
      switch (type) {
        case 'csv':
          // exportToCSV quotes a value that contains a comma; the names are written as they are.
          await exportToCSV(exportData, filename)
          break;
        case 'excel':
          await exportToExcel(exportData, filename)
          break;
      }
    } catch (error) {
      console.error('Export failed:', error)
      alert(t('exportImport.exportError'))
    }
  }

  const generateClientExport = async (type = 'csv') => {
    try {
      setIsLoading(true)
      const clientsSnapshot = await db.collection('clients').get()
      const clients = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      // Transform dates to readable format and create data with translated headers
      const exportData = clients.map(client => ({
        [t('exportImport.phone')]: client.phoneNumber || '',
        [t('exportImport.name')]: client.displayName || '',
        [t('exportImport.bonusValue')]: client.bonusValue || 0,
        [t('exportImport.totalPurchasedValue')]: client.totalPurchasedValue || 0,
        [t('exportImport.registrationDate')]: client.registrationDate?.toDate?.()?.toLocaleDateString() || '',
        [t('exportImport.lastPurchaseDate')]: client.lastPurchaseDate?.toDate?.()?.toLocaleDateString() || '',
      }))

      try {
        const filename = 'clients-export-' + format(new Date(), 'd-M-y-HH-mm')
        
        if (type === 'csv') {
          await exportToCSV(exportData, filename)
        } else {
          await exportToExcel(exportData, filename)
        }
      } catch (error) {
        console.error('Export failed:', error)
        alert(t('exportImport.exportError'))
      }
    } catch (error) {
      console.error('Export error:', error)
      alert(t('exportImport.exportError', {error: error.message}))
    } finally {
      setIsLoading(false)
    }
  }

  const productFields = [
    { label: 'ID', text: 'ID', value: 'id' },
    { label: t('exportImport.name'), text: t('exportImport.name'), value: 'name' },
    { label: t('exportImport.category'), text: t('exportImport.category'), value: 'category' },
    { label: t('exportImport.barcode'), text: t('exportImport.barcode'), value: 'barcode' },
    { label: t('exportImport.price'), text: t('exportImport.price'), value: 'price' },
    { label: t('exportImport.purchasePrice'), text: t('exportImport.purchasePrice'), value: 'purchasePrice' },
    { label: t('exportImport.inStock'), text: t('exportImport.inStock'), value: 'inStock' },
    { label: t('exportImport.supplier'), text: t('exportImport.supplier'), value: 'supplier' },
  ]

  const handleColumnMapping = (productField, importedField) => {
    setColumnMappingByName(prev => ({ ...prev, [productField.value]: importedField }))
  }

  const handleProductCategoryMapping = (category, fieldValue) => {
    setProductCategoryMapping(prev => ({ ...prev, [category]: fieldValue }))
  }

  const handleSupplierMapping = (supplier, fieldValue) => {
    setSupplierMapping(prev => ({ ...prev, [supplier]: fieldValue }))
  }

  const autoMapColumns = (importedData) => {
    let newColumnMapping = {}
    for (let index = 0; index < importedData[0].length; index++) {
      const importedColumnName = importedData[0][index]
      productFields.forEach((field, _) => {
        if (field.label.toLowerCase() === importedColumnName.toLowerCase()) {
          newColumnMapping[field.value] = {name: importedColumnName, text: importedColumnName, index: index}
        }
      })
    }
    let newImportedColumns = [...importedData[0].map((column, index) => ({name: column, index: index, text: column})), {index: -1, name: t('exportImport.ignore'), text: t('exportImport.ignore')}]
    setImportedColumns(newImportedColumns)
    setColumnMappingByName(newColumnMapping)
  }

  const autoMapProductCategories = () => {
    const newProductCategoryMapping = {}
    const productCategoryColumnIndex = columnMappingByName['category']?.index
    if (productCategoryColumnIndex === -1) {
      return
    }
    // array of all values in the productCategoriesDict
    const productCategoriesValues = Object.values(productCategoriesDict)
    // find all values in the productCategoryColumnKey that are not in productCategoriesDict
    for (let index = 1; index < importedData.length; index++) {
      const row = importedData[index]
      const category = row[productCategoryColumnIndex]
      if (!(category in newProductCategoryMapping)) {
        let newCategory = productCategoriesValues.find(existingCategory => existingCategory.name === category) || productCategoriesWithAdditionalOptions.find(existingCategory => existingCategory.id === 'new')
        newProductCategoryMapping[category] = newCategory
      }
    }
    setProductCategoryMapping(newProductCategoryMapping)
  }
  
  const autoMapSuppliers = () => {
    const newSupplierMapping = {}
    const supplierColumnIndex = columnMappingByName['supplier']?.index
    if (supplierColumnIndex === -1) {
      return
    }
    // array of all values in the suppliersDict
    const suppliersValues = Object.values(suppliersDict)
    for (let index = 1; index < importedData.length; index++) {
      const row = importedData[index]
      const supplier = row[supplierColumnIndex]
      if (!(supplier in newSupplierMapping)) {
        newSupplierMapping[supplier] = suppliersValues.find(existingSupplier => existingSupplier.name === supplier) || suppliersWithAdditionalOptions.find(existingSupplier => existingSupplier.id === 'new')
      }
    }
    setSupplierMapping(newSupplierMapping)
  }

  const importProducts = async () => {
    try {
      setImportProgress(1)

      // figure out the biggest barcode in the database
      let biggestBarcode = 0
      for (let value of Object.values(productsDict)) {
        if (!value.type && value.barcode > biggestBarcode && value.barcode < 210000) {
          biggestBarcode = value.barcode
        }
      }

      // find the biggest order from productCategoriesDict
      let biggestOrder = 0
      for (let value of Object.values(productCategoriesDict)) {
        if (value.order > biggestOrder) {
          biggestOrder = value.order
        }
      }

      let newCategoryMapping = {...productCategoryMapping}
      if (columnMappingByName['category'] !== -1) {
        // create new categories using centralized function with dual-write
        for (let categoryKey in productCategoryMapping) {
          if (productCategoryMapping[categoryKey].id === 'new') {
            let categoryInfo = {
              name: categoryKey,
              order: biggestOrder + 1,
              organizationId: context.user.organizationId,
              lastChangeBy: context.user.uid,
              lastChangeAt: new Date(),
              dailyStatID: getDailyStatID(null, new Date()),
              lastChangeStore: context.user.store.id,
            }
            biggestOrder += 1
            const newCategory = await saveNewProductCategory(db, categoryInfo)
            newCategoryMapping[categoryKey] = newCategory
          }
        }
      }
      let newSupplierMapping = {...supplierMapping}
      if (columnMappingByName['supplier'] !== -1) {
        // create new suppliers using centralized function with dual-write
        for (let supplierKey in supplierMapping) {
          if (supplierMapping[supplierKey].id === 'new') {
            let supplierInfo = {
              name: supplierKey,
              active: true,
              organizationId: context.user.organizationId,
              lastChangeBy: context.user.uid,
              lastChangeAt: new Date(),
              dailyStatID: getDailyStatID(null, new Date()),
              lastChangeStore: context.user.store.id,
            }
            const newSupplier = await saveNewSupplier(db, supplierInfo)
            newSupplierMapping[supplierKey] = newSupplier
          }
        }
      }
      
      for (let index = 1; index < importedData.length; index++) {
        setImportProgress(index)
        const row = importedData[index]
        var salePrices = {}
        var storeStock = {}
        var activeInStore = {}
        for (let key in storesDict) {
          salePrices[key] = (parseInt(row[columnMappingByName['price'].index]) || 0)
          storeStock[key] = columnMappingByName['inStock'] === -1 ? 0 : (parseInt(row[columnMappingByName['inStock'].index]) || 0)
          activeInStore[key] = true
        }
        var category = columnMappingByName['category'].index === -1 ? {id: 'other', name: t('exportImport.other')} : newCategoryMapping[row[columnMappingByName['category'].index]]
        var supplier = columnMappingByName['supplier'].index === -1 ? {id: 'other', name: t('exportImport.other')} : newSupplierMapping[row[columnMappingByName['supplier'].index]]
        var productUnit = Object.values(productUnitsDict).find(unit => unit.shortName === t('exportImport.pcs') || unit.shortName === t('exportImport.piece') || unit.shortName === t('exportImport.pieceLong'))
        var newValues = {
          name: row[columnMappingByName['name'].index],
          purchasePrice: parseFloat(row[columnMappingByName['purchasePrice'].index]),
          storeStock: storeStock,
          salePrices: salePrices,
          categoryID: category?.id, 
          supplierID: supplier?.id,
          barcode: columnMappingByName['barcode'].index === -1 ? (biggestBarcode + 1) : (parseInt(row[columnMappingByName['barcode'].index]) || (biggestBarcode + 1)),
          productUnit: productUnit?.id,
          packagePrice: 0,
          activeInStore: activeInStore,
          type: false,
          isComposite: false,
          manufacturedBeforeSale: false,
          ingredients: [],
          productImages: [],
          stockThreshold: 1,
          importance: 1,
          punchCardEnabled: false,
          isHiddenInNewSale: false,
          description: '',
          activeInEcommerce: false, 
          defaultQuantity: 1,
          productTags: [],
          dailyStatID: getDailyStatID(store, new Date()),
          lastChangeBy: context.user.uid,
          lastChangeAt: new Date(),
          lastChangeSource: 'import',
          lastChangeStore: context.user.store.id,
          organizationId: context.user.organizationId
        }
        let existingProduct = false
        if (productsByBarcodeDict[newValues.barcode]) {
          existingProduct = productsByBarcodeDict[newValues.barcode]
        }
        if (productsDict[newValues.id]) {
          existingProduct = productsDict[newValues.id]
        }
        if (existingProduct?.id && existingProductsUpdateSettings) {
          // update product using centralized function with dual-write
          console.log('updating product', newValues)
          await updateProduct(context.db, existingProduct, newValues, productsDict)
        }
        if (!existingProduct) {
          // create new product using centralized function with dual-write
          console.log('creating new product', newValues)
          await saveNewProduct(context.db, newValues)
        }
        biggestBarcode += 1
      }
      alert(t('exportImport.importDone'))
    } catch (error) {
      console.log('error', error)
      alert(t('exportImport.importError', {error: error.message}))
    }
  }

  // Both platforms go through the shared reader, so product import accepts .xlsx as well
  // as .csv (the sheet's `rows` are the same array-of-arrays this screen always used).
  const applyLoadedSheet = (sheet) => {
    setImportedData(sheet.rows)
    setShowColumnMappingUI(true)
    autoMapColumns(sheet.rows)
  }

  const importReadErrorText = (error) =>
    error instanceof MalformedImportFileError ? t('importFileMalformedQuotes', { row: error.row }) : error.message

  const handleFileSelect = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: IMPORT_DOCUMENT_PICKER_TYPES,
        copyToCacheDirectory: true,
      })
      if (!result.canceled) {
        const asset = result.assets[0]
        applyLoadedSheet(await readImportFileFromUri(asset.uri, asset.name))
      }
    } catch (error) {
      console.error('Error picking document:', error)
      Alert.alert(t('exportImport.importError', {error: importReadErrorText(error)}))
    }
  }

  // Web-specific file handling
  const onDrop = useCallback(async (acceptedFiles) => {
    try {
      applyLoadedSheet(await readImportFileFromWeb(acceptedFiles[0]))
    } catch (error) {
      console.error('Error reading import file:', error)
      Alert.alert(t('exportImport.importError', {error: importReadErrorText(error)}))
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = Platform.OS === 'web' ?
    ReactDropzone.useDropzone({
      onDrop,
      accept: IMPORT_DROPZONE_ACCEPT,
      multiple: false
    }) : { getRootProps: null, getInputProps: null, isDragActive: false }

  const UploadIcon = (props) => (
    <Icon {...props} name='upload-outline' width={24} height={24} fill={isDragActive ? theme['color-info-500'] : theme['text-hint-color']}/>
  )

  const renderImportSection = () => {
    if (Platform.OS === 'web') {
      return (
        <div {...getRootProps()} style={{...styles.dropzone, borderColor: isDragActive ? theme['color-info-500'] : theme['border-basic-color-3'], backgroundColor: isDragActive ? theme['color-info-transparent-100'] : theme['background-basic-color-2'], ...(isDragActive ? styles.dropzoneActive : {})}}>
          <input {...getInputProps()} />
          <View style={styles.uploadButton}>
            <UploadIcon />
            <Text style={{...styles.uploadButtonText, color: isDragActive ? theme['color-info-500'] : theme['text-hint-color'], ...(isDragActive ? styles.uploadButtonTextActive : {})}}>
              {isDragActive ? t('exportImport.dropFileHere') : t('exportImport.uploadProducts')}
            </Text>
          </View>
          <Text style={[styles.dropzoneText, { color: theme['text-hint-color'] }]}>
            {isDragActive ? t('exportImport.releaseToUpload') : t('exportImport.dragAndDropHint')}
          </Text>
        </div>
      )
    }

    return (
      <Button
        onPress={handleFileSelect}
        style={styles.mobileUploadButton}
      >
        {t('exportImport.uploadProducts')}
      </Button>
    )
  }

  return (
    <ScrollView style={{padding: 20}} contentContainerStyle={{alignItems: 'flex-start'}}>
      <Text category='h3' style={{marginTop: 20, marginBottom: 5}}>- {t('exportImport.productExport')}</Text>
      <HintView text={t('exportImport.exportProductsHint')} iconName='alert-circle-outline' style={{marginBottom: 10}} />
      <StoreChooserHorizontal store={store} showAll={false} onChange={(newStore) => {
        setStore(newStore)
      }} />
      {isLoading ? (
        <View style={{alignItems: 'center'}}><Spinner /></View>
      ) : (
        <View>
          <Button onPress={() => generateProductExport('csv')} style={{marginBottom: 10}}>
            {t('exportImport.generateProductExportCSV')}
          </Button>
          <Button onPress={() => generateProductExport('excel')}>
            {t('exportImport.generateProductExportExcel')}
          </Button>
        </View>
      )}

      <Text category='h3' style={{marginTop: 40, marginBottom: 5}}>- {t('exportImport.clientExport')}</Text>
      <HintView 
        text={t('exportImport.exportClientsHint')} 
        iconName='alert-circle-outline' 
        style={{marginBottom: 10}} 
      />
      
      {isLoading ? (
        <View style={{alignItems: 'center'}}><Spinner /></View>
      ) : (
        <View>
          <Button 
            onPress={() => generateClientExport('csv')} 
            style={{marginBottom: 10}}
          >
            {t('exportImport.generateClientExportCSV')}
          </Button>
          <Button 
            onPress={() => generateClientExport('excel')}
          >
            {t('exportImport.generateClientExportExcel')}
          </Button>
        </View>
      )}

      <View style={{width: '100%', marginTop: 20}}>
        <Text category='h3' style={{marginBottom: 10}}>- {t('salesImport.title')}</Text>
        <HintView text={t('salesImport.hint')} iconName='alert-circle-outline' style={{marginBottom: 10}} />
        <Button onPress={() => navigation.navigate('SalesImport')} style={{alignSelf: 'flex-start', marginBottom: 30}}>
          {t('salesImport.openImporter')}
        </Button>

        <Text category='h3' style={{marginBottom: 10}}>- {t('manufactureImport.title')}</Text>
        <HintView text={t('manufactureImport.hint')} iconName='alert-circle-outline' style={{marginBottom: 10}} />
        <Button onPress={() => navigation.navigate('ManufactureImport')} style={{alignSelf: 'flex-start', marginBottom: 30}}>
          {t('manufactureImport.openImporter')}
        </Button>

        <Text category='h3' style={{marginBottom: 10}}>- {t('exportImport.importProducts')}</Text>
        <HintView text={t('exportImport.importProductsHint')} iconName='alert-circle-outline' style={{marginBottom: 10}} />
        { !showColumnMappingUI && !importedData && renderImportSection()}
      </View>

      { showColumnMappingUI && importedData && (
        <View style={{width: '100%', marginTop: 20}}>
          <Text category='h6' style={{marginBottom: 10}}>{t('exportImport.previewImport')}</Text>
          <ScrollView 
            horizontal 
            style={styles.tableScroll}
            contentContainerStyle={[styles.tableContainer, { backgroundColor: theme['background-basic-color-1'] }]}
          >
            <Table style={styles.tableView} borderStyle={{borderWidth: 0}}>
              { importedData.slice(0, 6).map((rowData, index) => (
                <Row
                  key={index}
                  data={Object.values(rowData)}
                  style={styles.row}
                  textStyle={index === 0 ? styles.headerText : styles.text}
                />
              ))}
            </Table>
          </ScrollView>
          <Text style={styles.text}>{t('exportImport.totalRows', {count: importedData.length - 1})}</Text>
          
          <Text category='h6' style={{marginTop: 20, marginBottom: 10}}>{t('exportImport.mapColumns')}</Text>
          { productFields.map((field, index) => (
            <View key={index} style={styles.mappingRow}>
              <Text>{field.label}</Text>
              <PlatformDependantSelect
                data={importedColumns}
                style={styles.select}
                placeholder={t('exportImport.selectField')}
                selectedOption={columnMappingByName[field.value]}
                onSelect={(newValue) => handleColumnMapping(field, newValue)}
              />
            </View>
          ))}

          { !showCategoriesAndSuppliersUI && <Button onPress={() => {
            // check for required columns - name, price, purchasePrice
            if (!columnMappingByName['name'] || columnMappingByName['name'].index === -1 || !columnMappingByName['price'] || columnMappingByName['price'].index === -1 || !columnMappingByName['purchasePrice'] || columnMappingByName['purchasePrice'].index === -1  ) {
              Alert.alert(t('exportImport.requiredColumnsMissing'))
              return
            }
            autoMapProductCategories()
            autoMapSuppliers()
            setShowCategoriesAndSuppliersUI(true)
            }} style={{marginTop: 20}}
          >
            {t('exportImport.next')}
          </Button> }
        </View>
      )}

      { showCategoriesAndSuppliersUI && (
        <View style={{width: '100%', marginTop: 20}}>
          <Text category='h6' style={{marginBottom: 10}}>{t('exportImport.mapCategories')}</Text>
          { Object.keys(productCategoryMapping).map((category, index) => (
            <View key={category} style={styles.mappingRow}>
              <Text>{category}</Text>
              <PlatformDependantSelect
                data={productCategoriesWithAdditionalOptions}
                style={styles.select}
                placeholder={t('exportImport.selectCategory')}
                selectedOption={productCategoryMapping[category]}
                onSelect={(newValue) => handleProductCategoryMapping(category, newValue)}
              />
            </View>
          ))}

          <Text category='h6' style={{marginTop: 20, marginBottom: 10}}>{t('exportImport.mapSuppliers')}</Text>
          { Object.keys(supplierMapping).map((supplier, index) => (
            <View key={supplier} style={styles.mappingRow}>
              <Text>{supplier}</Text>
              <PlatformDependantSelect
                data={suppliersWithAdditionalOptions}
                style={styles.select}
                placeholder={t('exportImport.selectSupplier')}
                selectedOption={supplierMapping[supplier]}
                onSelect={(newValue) => handleSupplierMapping(supplier, newValue)}
              />
            </View>
          ))}

          <Text category='h6' style={{marginTop: 20, marginBottom: 10}}>{t('exportImport.updateExistingProductsSettings')}</Text>
          <HintView 
            text={t('exportImport.updateExistingProductsSettingsHint')} 
            iconName='alert-circle-outline' 
            style={{marginBottom: 10}}
          />
          <Radio style={{marginBottom: 5}} checked={existingProductsUpdateSettings === true} onChange={() => setExistingProductsUpdateSettings(true)}>{t('exportImport.update')}</Radio>
          <Radio style={{marginBottom: 10}} checked={existingProductsUpdateSettings === false} onChange={() => setExistingProductsUpdateSettings(false)}>{t('exportImport.doNotUpdate')}</Radio>
          
          {importProgress > 0 && importProgress < importedData.length-1 && 
            <View style={{flexDirection: 'row', alignItems: 'center'}} >
              <Spinner size='small' style={{marginRight: 10}}/>
              <Text>{t('exportImport.importProgress', {count: importProgress, totalCount: importedData.length-1})}</Text>
            </View>
          }

          {importProgress === 0 && <Button onPress={() => {
            importProducts()
          }} style={{marginTop: 20}}>
            {t('exportImport.import')}
          </Button>
          }
        </View>
      )}

      <View style={{height: 50}}></View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  row: { 
    minHeight: 48,  // Increased height for better touch targets
    borderBottomWidth: 1, 
    borderBottomColor: 'gray',
    flexDirection: 'row',
    alignItems: 'center',
  },
  text: { 
    fontSize: Platform.OS === 'web' ? 14 : 16, // Larger text on mobile
    marginRight: 10
  },
  headerText: {
    fontWeight: 'bold',
    fontSize: Platform.OS === 'web' ? 14 : 16,
    marginRight: 10
  },
  mappingRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 10 },
  select: { width: 200, marginLeft: 10 },
  dropzone: {
    alignSelf: 'stretch',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 8,
    padding: 20,
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  dropzoneActive: {
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 10,
  },
  uploadButtonText: {
    marginLeft: 10,
    fontSize: 16,
  },
  uploadButtonTextActive: {
  },
  dropzoneText: {
    marginTop: 10,
    fontSize: 14,
  },
  mobileUploadButton: {
    marginBottom: 20,
  },
  tableView: {
    marginBottom: 10,
    width: '100%',
  },
  tableContainer: {
    flexGrow: 0,
    marginBottom: 10,
    borderRadius: 10,
    paddingBottom: 10,
    paddingHorizontal: 10,
  },
  tableScroll: {
    width: '100%',
  },
})